import * as moment from "moment";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import app from "../../core/application";
import BackButton from "../../core/BackButton/BackButton";
import * as Constants from "../../core/constants";
import ExportButton from "../../core/ExportButton/ExportButton";
import Spinner from "../../core/Spinner/Spinner";
import TableCheckboxListFilter, { ICheckboxValue } from "../../core/TableCheckboxListFilter/TableCheckboxListFilter";
import TableDateRangeFilter from "../../core/TableDateRangeFilter/TableDateRangeFilter";
import TableTextFilter from "../../core/TableTextFilter/TableTextFilter";
import debounce from "../../helpers/debounce";
import { sessionGetItem, sessionSetItem } from "../../helpers/storage";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IMessageDetail from "../../types/IMessageDetail";
import IMessageDetailSearchRequest from "../../types/IMessageDetailSearchRequest";
import IResponse from "../../types/IResponse";
import "./MessageRecipientList.css";
import MessageRecipientRow from "./MessageRecipientRow";

interface IMessageRecipientListProps extends ITranslationProps, RouteComponentProps<any> {
    loading: boolean;
    search: (request: IMessageDetailSearchRequest) => Promise<IResponse<IListResults<IMessageDetail>> | null>;
}

interface IMessageRecipientListState {
    moreRecordsExist: boolean;
    messageText: string;
    startDate: Date;
    endDate: Date;
    messageTypes: string[];
    recipientText: string;
    page: number;

    messageRecipients: IMessageDetail[];
}

interface IMessageRecipientListPersistedState {
    messageText: string;
    startDate: string;
    endDate: string;
    messageTypes: string[];
    recipientText: string;
}

class MessageRecipientList extends React.Component<IMessageRecipientListProps, IMessageRecipientListState> {
    private exportForm: HTMLFormElement | null;
    private stateStorageKey: string = "messageRecipientList_state_storage";
    private searchWithDebounce = debounce(() => { this.search(); }, 500);
    private defaultStartDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).add(-1, "month").toDate();
    private defaultEndDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).toDate();
    private messageTypeValues: ICheckboxValue[] = [
        { key: Constants.MESSAGE_TYPE_EMAIL, value: Constants.MESSAGE_TYPE_EMAIL },
        { key: Constants.MESSAGE_TYPE_PUSH, value: Constants.MESSAGE_TYPE_PUSH },
        { key: Constants.MESSAGE_TYPE_SMS, value: Constants.MESSAGE_TYPE_SMS },
        { key: Constants.MESSAGE_TYPE_VOICE, value: Constants.MESSAGE_TYPE_VOICE },
    ];

    constructor(props: IMessageRecipientListProps) {
        super(props);

        this.state = {
            moreRecordsExist: false,
            startDate: this.defaultStartDate,
            endDate: this.defaultEndDate,
            page: 1,
            messageText: "",
            messageTypes: [],
            recipientText: "",
            messageRecipients: []
        };

        this.loadNextPage = this.loadNextPage.bind(this);
        this.search = this.search.bind(this);
        this.updateDateRange = this.updateDateRange.bind(this);
        this.updateMessageText = this.updateMessageText.bind(this);
        this.updateRecipientText = this.updateRecipientText.bind(this);
        this.updateMessageTypes = this.updateMessageTypes.bind(this);
        this.onRowClick = this.onRowClick.bind(this);
        this.onExportClick = this.onExportClick.bind(this);
    }

    public componentDidMount() {
        const savedState = sessionGetItem<IMessageRecipientListPersistedState>(this.stateStorageKey);

        if (savedState) {
            this.setState({
                messageText: savedState.messageText,
                messageTypes: savedState.messageTypes,
                startDate: moment(savedState.startDate).toDate(),
                endDate: moment(savedState.endDate).toDate()
            }, () => { this.search(true) });
        } else {
            this.search(true);
        }
    }

    public componentWillUnmount() {

        const persistedState: IMessageRecipientListPersistedState = {
            messageText: this.state.messageText,
            recipientText: this.state.recipientText,
            messageTypes: this.state.messageTypes,
            startDate: this.state.startDate.toJSON(),
            endDate: this.state.endDate.toJSON()
        };

        sessionSetItem(this.stateStorageKey, persistedState);
    }

    public render() {
        const application = app();
        const dlToken = application.userSession ? application.userSession.fileDownloadAuthenticationToken : "";
        const recipientRows: React.ReactNode[] = [];
        this.state.messageRecipients.forEach((recipient, idx) => {
            recipientRows.push(<MessageRecipientRow key={idx} onclick={this.onRowClick} {...recipient} />);
        });
        const messageTypeInputs: React.ReactNode[] = [];
        this.state.messageTypes.forEach((messageType, index) => {
            messageTypeInputs.push(<input key={index} type="hidden" name="messageTypes[]" value={messageType} />);
        });

        return (
            <div>
                <BackButton />

                {this.props.loading && <Spinner />}

                <h2>{this.props.t("MessageRecipientList.Title")}</h2>

                <div className="float-right">
                    <ExportButton onClick={this.onExportClick} />
                    <form ref={ref => this.exportForm = ref} method="POST" style={{ "display": "none" }}
                        action={`${application.webServiceBaseURL}/api/messages/exportmessagedetail?dltoken=${dlToken}`}
                        target="_blank">
                        <input type="hidden" name="Text" value={this.state.messageText} />
                        <input type="hidden" name="Recipient" value={this.state.recipientText} />
                        <input type="hidden" name="StartDate" value={this.state.startDate.toJSON()} />
                        <input type="hidden" name="EndDate" value={this.state.endDate.toJSON()} />
                        {messageTypeInputs}
                    </form>
                </div>

                <table className="table table-sm messagerecipientlist-table">
                    <thead className="thead-light">
                        <tr>
                            <th>
                                <div>{this.props.t("MessageDetail.Recipient")}</div>
                                <TableTextFilter onChange={this.updateRecipientText} searchButtonClick={this.search} value={this.state.recipientText} />
                            </th>
                            <th>
                                <div>{this.props.t("MessageList.MessageText")}</div>
                                <TableTextFilter onChange={this.updateMessageText} searchButtonClick={this.search} value={this.state.messageText} />
                            </th>
                            <th>
                                <div>{this.props.t("MessageList.MessageDate")}</div>
                                <TableDateRangeFilter small={true} startDate={this.state.startDate} endDate={this.state.endDate} onChange={this.updateDateRange} />
                            </th>
                            <th style={{ width: "15%" }}>
                                <div>{this.props.t("MessageList.MessageType")}</div>
                                <TableCheckboxListFilter values={this.messageTypeValues} onChange={this.updateMessageTypes} selectedValues={this.state.messageTypes} />
                            </th>
                        </tr>
                    </thead>
                </table>
                <table className="table table-sm messagerecipientlist-table table-hover">
                    <tbody>
                        <tr>
                            <th>{this.props.t("MessageDetail.Recipient")}</th>
                            <th>{this.props.t("MessageList.ReferenceID")}</th>
                            <th>{this.props.t("MessageList.Description")}</th>
                            <th>{this.props.t("MessageList.Content")}</th>
                            <th style={{ width: "150px" }}>{this.props.t("Global.Date")}</th>
                            <th>{this.props.t("Global.Type")}</th>
                        </tr>
                        {recipientRows}
                    </tbody>
                </table>
                {
                    this.state.moreRecordsExist &&
                    <button data-id="showMoreButton" className="btn btn-outline-secondary" onClick={this.loadNextPage}>{this.props.t("Global.ShowMore")}
                    </button>
                }
            </div>
        );
    }

    private loadNextPage() {

        this.setState({ page: this.state.page + 1 }, () => { this.search(false) });
    }

    private search(newSearch: boolean = true) {

        this.setState({ page: newSearch ? 1 : this.state.page }, async () => {

            const response = await this.props.search(this.getSearchRequest());
            if (response && response.success) {
                if (newSearch) {
                    this.setState({ messageRecipients: response.payload.records, moreRecordsExist: response.payload.moreRecordsExist });
                } else {
                    this.setState((state: IMessageRecipientListState) => {
                        state.messageRecipients.push(...response.payload.records);
                        state.moreRecordsExist = response.payload.moreRecordsExist;
                        return state;
                    });
                }
            }
        });
    }

    private getSearchRequest(): IMessageDetailSearchRequest {
        return {
            endDate: this.state.endDate.toJSON(),
            startDate: this.state.startDate.toJSON(),
            messageTypes: this.state.messageTypes,
            page: this.state.page,
            recipient: this.state.recipientText,
            text: this.state.messageText
        };
    }

    private updateMessageText(text: string) {
        this.setState({ messageText: text, page: 1 }, this.searchWithDebounce);
    }

    private updateRecipientText(text: string) {
        this.setState({ recipientText: text, page: 1 }, this.searchWithDebounce);
    }

    private updateDateRange(newStartDate: Date | null, newEndDate: Date | null) {
        const startDate = newStartDate ? newStartDate : this.defaultStartDate;
        const endDate = newEndDate ? newEndDate : this.defaultEndDate;

        if (startDate !== this.state.startDate || endDate !== this.state.endDate) {
            this.setState({ startDate, endDate, page: 1 }, this.searchWithDebounce);
        }
    }

    private updateMessageTypes(selectedItems: string[]) {
        this.setState({ messageTypes: selectedItems }, this.searchWithDebounce);
    }

    private onRowClick(messageID: number, messageType: string) {
        this.props.history.push(`/message/${messageID}/${messageType}`);
    }

    private onExportClick() {
        if (this.exportForm) {
            this.exportForm.submit();
        }
    }

}

export default withRouter(translateComponent(MessageRecipientList));



// WEBPACK FOOTER //
// ./src/components/MessageRecipientList/MessageRecipientList.tsx